// gameengine/gameAdvanceDay.js
import { processSquadHealing } from './gameSquad';
import { processFacilityConstruction } from './gameFacilities';
import { processResearch } from './gameResearch';
import { processMissions } from './gameMissions';
import { processResourceGeneration } from './gameResources';
import { generateDayEvents } from './gameEvents';

/**
 * Advances the campaign by one day (pure function with no side effects)
 * @param {Object} currentCampaign - Current campaign object with id and day
 * @param {Object} gameState - Current game state containing all relevant data
 * @param {Object} eventLibrary - Theme event library with all event pools
 * @returns {Object} New game state with all updates applied
 */
export const advanceDay = (currentCampaign, gameState, eventLibrary = {}) => {
  if (!currentCampaign?.id) {
    throw new Error('Invalid campaign: Missing campaign ID');
  }

  const newDay = (currentCampaign.day || 1) + 1;

  // Process all day-dependent mechanics
  const updatedState = {
    day: newDay,
    squad: processSquadHealing(gameState.squad, gameState.facilities),
    facilities: processFacilityConstruction(gameState.facilities),
    research: processResearch(gameState.currentResearch, gameState.researchList),
    missions: processMissions(gameState.activeMissions || [], gameState.squad),
    resources: processResourceGeneration(gameState.resources, gameState.facilities),
    events: generateDayEvents(newDay, gameState, eventLibrary)  // ← Pass eventLibrary here
  };

  return updatedState;
};

/**
 * Gets a summary of what will happen when advancing the day
 */
export const getAdvanceDaySummary = (gameState) => {
  const summary = {
    healing: [],
    completions: [],
    research: null,
    missions: [],
    resources: {}
  };

  gameState.squad?.forEach(member => {
    if (member.health < member.max_health && member.status !== 'KIA' && member.status !== 'On Mission') {
      summary.healing.push({
        name: member.name,
        currentHealth: member.health,
        healAmount: 5
      });
    }
  });

  gameState.facilities?.forEach(facility => {
    if (facility.construction_days_remaining === 1) {
      summary.completions.push({
        type: 'facility',
        name: facility.name,
        facilityType: facility.type
      });
    }
  });

  if (gameState.currentResearch) {
    const progressPerDay = 100 / (gameState.currentResearch.duration || 1);
    if (gameState.currentResearch.progress + progressPerDay >= 100) {
      summary.research = {
        name: gameState.currentResearch.name,
        type: gameState.currentResearch.type
      };
    }
  }

  gameState.activeMissions?.forEach(mission => {
    if (mission.days_remaining === 1) {
      summary.missions.push({
        name: mission.name,
        type: mission.type,
        squadSize: mission.squad?.length || 0
      });
    }
  });

  return summary;
};